import React, { useMemo, useState } from 'react';
import I18n from 'modules/i18n';
import CorrelationsSettingsModal from '@/components/shared/Correlations/CorrelationsSettingsModal';
import Spinner from '@/components/common/Spinner';
import CorrelationsContext from '@/components/common/Context';
import Pagination from '@/components/common/libraries/common/Pagination/Pagination';
import useFlags from '@/hooks/useFlags';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/utils/ReactQuery';
import BreadcrumbsNavigator from '@/components/shared/Breadcrumbs/BreadcrumbsNavigator';
import cn from '@/utils/cn';
import TagCard from './TagCard/TagCard';
import Filters from './Filters';
import SearchBar from './SearchBar';
import useCorrelationsStore from './useCorrelationsStore';
import useTags from './hooks/useTags';
import searchIcon from './search.svg';

interface CorrelationProps {
  grade: number | null;
  isSysadmin?: boolean;
  programId: number;
  stafferId: number | null;
  state: string;
  stateId?: number | null;
  useLargerFont?: boolean;
}

const PER_PAGE = 20;

const Correlations = ({
  grade: initialGrade, isSysadmin = false, programId, stafferId, state, stateId = null, useLargerFont,
}: CorrelationProps) => {
  const { correlationsFilters, showAdvancedStandards } = useFlags();
  // The tree follows whatever framework the cards show (EdGate or CPALMS); the server decides.
  const showStandardsFilter = !!correlationsFilters && stateId != null;
  const filteredTags = useCorrelationsStore(s => s.filteredTags);
  const lessonsCheckedIds = useCorrelationsStore(s => s.lessonsCheckedIds);
  const standardsCheckedIds = useCorrelationsStore(s => s.standardsCheckedIds);
  const [currentPage, setCurrentPage] = useState(1);
  const [grade, setGrade] = useState(initialGrade);
  const clearStandards = useCorrelationsStore(s => s.clearStandards);
  const setTags = useCorrelationsStore(s => s.setTags);
  const lessonIds = useMemo(() => [...lessonsCheckedIds].sort((a, b) => a - b), [lessonsCheckedIds]);
  const standardIds = useMemo(() => [...standardsCheckedIds].sort((a, b) => a - b), [standardsCheckedIds]);
  const filterKey = `${standardIds.join(',')}|${lessonIds.join(',')}`;

  // Reset during render (not in an effect) so the query below never fetches the old page with the
  // new selection.
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setCurrentPage(1);
  }

  const { data: { filter_options: filterOptions, tags = [], pagination }, isFetching, isLoading } = useTags({
    lessonIds, page: currentPage, perPage: PER_PAGE, programId, setTags, standardIds,
  });
  const lessonOptions = correlationsFilters ? (filterOptions?.units ?? []) : undefined;
  const filtersActive = standardIds.length > 0 || lessonIds.length > 0;

  const handleGradeSaved = (newGrade: number) => {
    clearStandards();
    setGrade(newGrade);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    // Scroll to top of content
    window.scrollTo({ behavior: 'smooth', top: 0 });
  };

  const renderHeader = () => (
    <div className="tw-flex tw-justify-between">
      <div className="tw-flex tw-flex-col tw-justify-end tw-basis-60"> {/* Match Filters width */}
        <BreadcrumbsNavigator
          largerFont={useLargerFont}
          page="home"
          pageTitle={I18n.t('correlations')}
          programId={programId}
        />
        <h1 className="heading2 tw-pb-0 tw-mt-2">{I18n.t('correlations')}</h1>
      </div>

      <div className="tw-pl-2.5 tw-flex tw-justify-between tw-items-end tw-flex-1">
        <SearchBar />

        <div>
          {stafferId && showAdvancedStandards && (
            <CorrelationsSettingsModal
              grade={grade}
              programId={programId}
              stafferId={stafferId}
              state={state}
              onSaved={handleGradeSaved}
            />
          )}
          {pagination && (
            <div className="tw-flex tw-justify-end">
              <Pagination
                currentPage={pagination.current_page}
                pageSize={pagination.per_page}
                totalCount={pagination.total_tags}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderTags = () => {
    if (tags.length === 0) {
      const messageKey = lessonOptions && filtersActive ? 'no_correlations_for_filters' : 'no_correlations_message';

      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-py-16">
          <div className="tw-bg-gray-200 tw-rounded-full tw-w-16 tw-h-16 tw-flex tw-items-center tw-justify-center">
            <img alt="magnifying glass" src={searchIcon} />
          </div>
          <div className="tw-text-2xl tw-font-bold">{I18n.t('no_results')}</div>
          <div>{I18n.t(messageKey)}</div>
        </div>
      );
    }
    const tagsToRender = !lessonOptions && filteredTags.length > 0 ? filteredTags : tags;

    return (
      tagsToRender.map(tag => (
        <TagCard
          key={tag.id}
          anchored={window.location.hash.slice(1) === tag.alias}
          isSysadmin={isSysadmin}
          tag={tag}
        />
      ))
    );
  };

  return (
    <CorrelationsContext.Provider value={{ programId }}>
      {renderHeader()}

      <div className="tw-flex">
        {!isLoading && (showStandardsFilter || (lessonOptions ? lessonOptions.length > 0 : tags.length > 0)) && (
          <Filters
            filterData={tags}
            grade={grade}
            lessonOptions={lessonOptions}
            programId={programId}
            showStandards={showStandardsFilter}
            stateId={stateId}
          />
        )}
        <section
          aria-busy={isFetching}
          aria-label="Benchmarks container"
          className={cn('tw-flex-1', isFetching && !isLoading && 'tw-opacity-60 tw-pointer-events-none')}
        >
          {isLoading ? <Spinner /> : renderTags()}

          {pagination && (
            <div className="tw-flex tw-justify-end">
              <Pagination
                currentPage={pagination.current_page}
                pageSize={pagination.per_page}
                totalCount={pagination.total_tags}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </section>
      </div>
    </CorrelationsContext.Provider>
  );
};

const CorrelationsWrapper = (props: CorrelationProps) => (
  <QueryClientProvider client={queryClient}>
    <Correlations {...props} />
  </QueryClientProvider>
);

export default CorrelationsWrapper;
